import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import type {
    UIDataContentType,
    UIDataContentTypes,
    UIDataElementBodyData,
    UIDataMethod,
    UIDataMethods,
    UIDataType,
    UIDataTypes,
} from './ui-data.type';
import type {
    ErrorHandler,
    NullOr,
    ResponseCallback,
    ResponseCode,
} from '../../global/utils.type';
import { ResponseStatusCode } from '../../global/constants';
import styles from './ui-data.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIData
 * @element ui-data
 * @classdesc Represents a class for <code>ui-data</code> element.
 * Defaults abstract element which holds the data. Also
 * Makes ajax request for resources specified in 'src' attribute
 * or read inline from element body JSON, CSV, Plain Text and
 * other formats.
 * @property {string} [src] {@attr src} URL source of data.
 * @property {string} [type="text/plain"] {@attr type} application/json, text/csv, text/plain etc.
 * @property {string} [method="GET"] {@attr method} HTTP method of request.
 * At the moment only GET method is supported.
 * @slot
 * @example
 * <ui-data src="http://www.swedbank.com/page.html></ui-data>
 * <ui-data src="http://www.swedbank.com/page.json type="application/json"></ui-data>
 * <ui-data type="application/json">{
 *   "name": "Salomon",
 *   "age": 34,
 *   "job": "Developer"
 * }</ui-data>
 *
 * <ui-data type="text/csv">
 *   Name,Age,Job
 *   Bill,34,Cowboy
 *   Batman,120,Superhero
 * </ui-data>
 */
@Component({ tag: 'ui-data', styles })
export class UIData extends UIElement {
    static readonly UIDataType: UIDataTypes = {
        TextPlain: 'text/plain',
        ApplicationJson: 'application/json',
        TextJson: 'text/json',
        TextCsv: 'text/csv',
    } as const;

    static readonly UIDataMethod: UIDataMethods = {
        Get: 'GET',
        Post: 'POST',
        Put: 'PUT',
        Patch: 'PATCH',
        Delete: 'DELETE',
    } as const;

    static readonly UIDataContentType: UIDataContentTypes = {
        Text: 'text',
        Json: 'json',
    };

    @Attribute(String) src: string = '';
    @Attribute(String) type: UIDataType = UIData.UIDataType.TextPlain;
    @Attribute(String) method: UIDataMethod = UIData.UIDataMethod.Get;

    private _errorHandler?: ErrorHandler<unknown>;
    private _fetchedData: string | object | null = null;
    callback?: ResponseCallback;

    /**
     * Error handler.
     * @returns {Function}
     */
    get errorHandler(): ErrorHandler<unknown> {
        return this._errorHandler ?? this.defaultErrorHandler.bind(this);
    }

    /**
     * @param {Function} handler
     */
    set errorHandler(handler: ErrorHandler<unknown>) {
        this._errorHandler = handler;
    }

    /**
     * Default error handler.
     * @param {Error} error
     * @private
     */
    private defaultErrorHandler(error: unknown): void {
        const errorMessage =
            this.constructor.name + ': cannot fetch ' + this.src + '.';
        console.error(error, errorMessage);
    }

    /**
     * Resolves XHR responseType string based on type.
     * @returns {string}
     * @private
     */
    private get resolveContentType(): UIDataContentType {
        switch (this.type) {
            case UIData.UIDataType.ApplicationJson:
            case UIData.UIDataType.TextJson:
                return UIData.UIDataContentType.Json;
            default:
                return UIData.UIDataContentType.Text;
        }
    }

    /**
     * Checks valid AJAX status
     * @param {number} status
     * @returns {boolean}
     */
    static isStatusValid(status: ResponseCode | number): boolean {
        return (
            status >= ResponseStatusCode.Success &&
            status < ResponseStatusCode.BadRequest
        );
    }

    /**
     * Fetches content from remove server's request (AJAX).
     * @private
     */
    private fetchRemoteData(): void {
        fetch(this.src, {
            method: this.method,
            headers: { 'Content-Type': this.type },
        })
            .then((response: Response): Promise<void> | void => {
                if (UIData.isStatusValid(response.status)) {
                    const call =
                        this.type === UIData.UIDataType.ApplicationJson
                            ? response.json()
                            : response.text();
                    return call.then((data: unknown) => {
                        this._fetchedData = data as string | object;
                        this.callback?.(data);
                    });
                } else {
                    this.errorHandler(response);
                }
            })
            .catch((error: unknown) => {
                this.errorHandler(error);
            });
    }

    /**
     * Fetches the data from content which is put into this element innerHTML.
     * @returns {object | string}
     * @private
     */
    private fetchElemBodyData(): NullOr<UIDataElementBodyData> {
        if (this.resolveContentType === UIData.UIDataContentType.Json) {
            if (this.innerHTML.trim() === '') {
                this._fetchedData = {};
            } else {
                try {
                    this._fetchedData = JSON.parse(this.innerHTML);
                } catch (e) {
                    console.error(e);
                    this._fetchedData = {};
                }
            }
        } else {
            this._fetchedData = this.innerHTML.trim();
        }
        if (this.callback) {
            this.callback(this._fetchedData);
        }
        return this.getFetchedData();
    }

    /**
     * Gets the fetched data.
     * @returns {undefined|object|string}
     */
    getFetchedData(): NullOr<UIDataElementBodyData> {
        return this._fetchedData;
    }

    /**
     * Fetches the data from URL if 'src' attribute is provided,
     * otherwise from elements innerHTML.
     * @param {Function} callback Callback after data fetching.
     * @returns {object | string}
     */
    get(callback?: ResponseCallback): unknown {
        if (callback) {
            this.callback = callback;
        }
        return this.src ? this.fetchRemoteData() : this.fetchElemBodyData();
    }
}
